import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import {
  listOtherCharges,
  createOtherCharge,
  updateOtherCharge,
} from "../erpClient";
import {
  parseApiPagination,
  extractListRowsFromResponse,
} from "../utils/parseApiPagination";
import {
  useVisitedPageCache,
  visitedPageKey,
} from "../utils/visitedPageCache";
import { createErpSocket } from "../utils/socketIo";
import { hasPermission, PERMISSIONS } from "../utils/permissions";

const RATE_ON_OPTIONS = ["FIXED", "PCS", "CT", "WT"];

const emptyForm = {
  name: "",
  code: "",
  rateOn: "",
  rate: "",
  status: "1",
};

const isBlank = (value) => value == null || String(value).trim() === "";

const displayRate = (rate) => {
  if (isBlank(rate)) return "";
  const num = Number(rate);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const displayText = (value) => (isBlank(value) ? "" : String(value));

const getApiMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  return fallback;
};

const validateForm = (form) => {
  if (isBlank(form.name)) return "Name is required";
  if (isBlank(form.code)) return "Code is required";
  if (!isBlank(form.rate)) {
    const text = String(form.rate).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(text)) {
      return "Rate must be a number greater than or equal to 0, with up to 2 decimals";
    }
  }
  return "";
};

const toPayload = (form) => ({
  name: String(form.name).trim(),
  code: String(form.code).trim(),
  rate_on: isBlank(form.rateOn) ? null : String(form.rateOn).trim(),
  rate: isBlank(form.rate) ? null : Number(String(form.rate).trim()),
  status: String(form.status) === "0" ? 0 : 1,
});

const normalizeRateOn = (value) => {
  if (isBlank(value)) return "";
  const upper = String(value).trim().toUpperCase();
  return RATE_ON_OPTIONS.includes(upper) ? upper : String(value).trim();
};

const mapRow = (row) => {
  const rateValue =
    row.rate !== undefined && row.rate !== null
      ? row.rate
      : row.price !== undefined && row.price !== null
        ? row.price
        : "";
  return {
    id: row.id,
    name: row.name || "",
    code: row.code || row.short_code || "",
    rateOn: normalizeRateOn(
      row.rate_on ?? row.rateOn ?? row.price_to ?? row.priceTo ?? "",
    ),
    rate: rateValue,
    status:
      row.status !== null && row.status !== undefined
        ? String(row.status)
        : "1",
  };
};

function OtherChargeTable() {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const canAdd =
    hasPermission(PERMISSIONS.OTHER_CHARGE_ADD) ||
    hasPermission(PERMISSIONS.ITEM_CATEGORY_ADD) ||
    hasPermission(PERMISSIONS.METAL_GROUP_ADD);
  const canEdit =
    hasPermission(PERMISSIONS.OTHER_CHARGE_EDIT) ||
    hasPermission(PERMISSIONS.ITEM_CATEGORY_EDIT) ||
    hasPermission(PERMISSIONS.METAL_GROUP_EDIT);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const pageCache = useVisitedPageCache();

  const fetchRows = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey(["rate_on_v1", page, limit, search.trim()]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setRows(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await listOtherCharges({
          search: search.trim(),
          page,
          limit,
        });
        const items = extractListRowsFromResponse(response, ["other_charges"]);
        if (response?.success === false) {
          toast.error(response?.message || "Failed to fetch other charges");
          setRows([]);
          setTotalCount(0);
          return;
        }
        const fetched = (Array.isArray(items) ? items : []).map(mapRow);
        fetched.sort((a, b) => Number(b.id) - Number(a.id));
        const total = parseApiPagination(response, fetched.length).total;
        setRows(fetched);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: fetched, total });
      } catch (error) {
        console.error("Error fetching other charges:", error);
        toast.error(getApiMessage(error, "Failed to fetch other charges"));
        setRows([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [pageCache],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchRows(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchRows]);

  useEffect(() => {
    const handleApiUrlChange = () => {
      setCurrentPage(1);
      fetchRows(1, itemsPerPage, searchQuery, true);
    };
    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => window.removeEventListener("apiUrlChanged", handleApiUrlChange);
  }, [itemsPerPage, searchQuery, fetchRows]);

  useEffect(() => {
    const socket = createErpSocket();
    const applyRow = (incoming, isCreate) => {
      const charge = incoming?.other_charge || incoming;
      if (!charge?.id) return;
      const next = mapRow(charge);
      setRows((prev) => {
        const exists = prev.some((row) => row.id === next.id);
        if (exists) {
          return prev.map((row) => (row.id === next.id ? { ...row, ...next } : row));
        }
        if (!isCreate) return prev;
        return [next, ...prev].sort((a, b) => Number(b.id) - Number(a.id));
      });
    };

    socket.on("other_charge_updated", (data) => applyRow(data, false));
    socket.on("other_charge_created", (data) => applyRow(data, true));

    return () => {
      socket.off("other_charge_updated");
      socket.off("other_charge_created");
      socket.disconnect();
    };
  }, []);

  const openAdd = () => {
    if (!canAdd) {
      toast.error("You do not have permission to add other charges.");
      return;
    }
    setForm(emptyForm);
    setEditingId(null);
    setShowAddModal(true);
  };

  const openEdit = (row) => {
    if (!canEdit) {
      toast.error("You do not have permission to edit other charges.");
      return;
    }
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      code: row.code || "",
      rateOn: normalizeRateOn(row.rateOn),
      rate: isBlank(row.rate) ? "" : String(row.rate),
      status: String(row.status) === "0" ? "0" : "1",
    });
    setShowEditModal(true);
  };

  const closeModals = () => {
    if (isSaving) return;
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async (event) => {
    event?.preventDefault?.();
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    const payload = toPayload(form);
    setIsSaving(true);
    try {
      const response = editingId
        ? await updateOtherCharge({ id: editingId, ...payload })
        : await createOtherCharge(payload);

      if (response?.success === false) {
        toast.error(response?.message || "Failed to save other charge");
        return;
      }

      toast.success(
        response?.message ||
          (editingId ? "Other charge updated" : "Other charge created"),
      );
      setShowAddModal(false);
      setShowEditModal(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchRows(currentPage, itemsPerPage, searchQuery, true);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error(getApiMessage(error, "This code is already used"));
      } else {
        toast.error(getApiMessage(error, "Failed to save other charge"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRows = rows.filter((row) => {
    if (statusFilter === "") return true;
    const active = row.status === "1" || row.status === 1;
    if (statusFilter === "Active") return active;
    if (statusFilter === "Inactive") return !active;
    return true;
  });

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const renderFormFields = (statusName) => (
    <>
      <div className="mb-3">
        <Form.Label>
          Name <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          type="text"
          className={buttonClass}
          placeholder="Enter name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
        />
      </div>
      <div className="mb-3">
        <Form.Label>
          Code <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          type="text"
          className={buttonClass}
          placeholder="Enter code"
          value={form.code}
          onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
        />
      </div>
      <div className="mb-3">
        <Form.Label>Rate On</Form.Label>
        <Form.Select
          className={buttonClass}
          value={form.rateOn}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, rateOn: e.target.value }))
          }
        >
          <option value="">Select Rate On</option>
          {RATE_ON_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
      </div>
      <div className="mb-3">
        <Form.Label>Rate</Form.Label>
        <Form.Control
          type="text"
          inputMode="decimal"
          className={buttonClass}
          placeholder="Optional"
          value={form.rate}
          onChange={(e) => setForm((prev) => ({ ...prev, rate: e.target.value }))}
        />
      </div>
      <div className="mb-3">
        <Form.Label>Status</Form.Label>
        <div className="d-flex gap-3">
          <Form.Check
            type="radio"
            label="Active"
            name={statusName}
            value="1"
            checked={String(form.status) === "1"}
            onChange={() => setForm((prev) => ({ ...prev, status: "1" }))}
          />
          <Form.Check
            type="radio"
            label="Inactive"
            name={statusName}
            value="0"
            checked={String(form.status) === "0"}
            onChange={() => setForm((prev) => ({ ...prev, status: "0" }))}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Other Charge Table</h4>
            {canAdd && (
              <button
                type="button"
                className={`btn btn-success ${buttonClass}`}
                onClick={openAdd}
              >
                Add Other Charge
              </button>
            )}
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-3 ${buttonClass}`}
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Rate On</th>
                    <th>Rate</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredRows.length > 0 ? (
                    filteredRows.map((row) => {
                      const active = row.status === "1" || row.status === 1;
                      return (
                        <tr key={row.id}>
                          <td>{row.name}</td>
                          <td>{row.code}</td>
                          <td>{displayText(row.rateOn)}</td>
                          <td>{displayRate(row.rate)}</td>
                          <td>
                            <span
                              className={`badge ${
                                active
                                  ? "bg-success-subtle text-success"
                                  : "bg-danger-subtle text-danger"
                              }`}
                            >
                              {active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            {canEdit && (
                              <button
                                type="button"
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                onClick={() => openEdit(row)}
                              >
                                <Icon
                                  icon="solar:pen-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No other charges found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={(next) => {
                setItemsPerPage(next);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      <Modal
        show={showAddModal}
        onHide={closeModals}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Other Charge</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate onSubmit={handleSave}>
            {renderFormFields("addOtherChargeStatus")}
            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Submit"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={closeModals}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Other Charge</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>{renderFormFields("editOtherChargeStatus")}</Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default OtherChargeTable;
